import React, { useState, useEffect, useCallback } from 'react';
import { Search, MapPin, Calendar, Users, Loader2, X, ExternalLink, CheckCircle, AlertCircle, XCircle, Eye, FileText, Clock, Info, ChevronLeft, ChevronRight, Copy, Check, ChevronDown } from 'lucide-react';
import EventLocationViewer from './EventLocationViewer';
import { usePublicEvents } from '../hooks/usePublicEvents';
import { useAuth } from '../contexts/AuthContext';
import { useEventImage } from '../hooks/useEventThumbnail';
import Login from './Login';
import CityFilters from './CityFilters';
import { goToEventDetail, goToLogin } from '../utils/navigation';
import AdSense from './AdSense';

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface SearchDiscoveryProps {
  eventId?: string | null;
  isVisible?: boolean; // Add prop to control visibility
}

const SearchDiscovery: React.FC<SearchDiscoveryProps> = ({ eventId, isVisible = true }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [pendingEvent, setPendingEvent] = useState<any>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Filter modal states
  
  // Debounce search term with 500ms delay
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  const { events, loading, error } = usePublicEvents({
    search: debouncedSearchTerm,
    page: 0,
    limit: 8, // Limited to 8 events for home page
    enabled: isVisible // Only make API calls when component is visible
  });
  const { user } = useAuth();
  
  // Handle city selection
  const handleCitySelect = (city: string | null) => {
    setSelectedCity(city);
    // Update search term when city is selected
    if (city) {
      setSearchTerm(city);
    } else {
      setSearchTerm('');
    }
    };
  
  // Clear all filters
  const clearAllFilters = () => {
    setSelectedCity(null);
    setSearchTerm('');
  };
  
  // Auto-show specific event when eventId is provided
  useEffect(() => {
    if (eventId && events.length > 0) {
      const targetEvent = events.find(event => 
        (event.EVENT_ID || event.id)?.toString() === eventId
      );
      
      if (targetEvent) {
        const eventDisplay = {
          id: targetEvent.EVENT_ID || targetEvent.id || Math.random(),
          title: targetEvent.TITLE || targetEvent.title || 'Untitled Event',
          location: targetEvent.LOCATION || targetEvent.location || targetEvent.ADDRESS || targetEvent.address || 'Location TBA',
          attendees: formatAttendees(targetEvent.EXPECTED_ATTENDANCE || targetEvent.expectedAttendance),
          date: formatDateRange(targetEvent.START_DATE || targetEvent.startDate, targetEvent.END_DATE || targetEvent.endDate),
          isFeatured: targetEvent.IS_FEATURED || targetEvent.isFeatured || false,
          originalEvent: targetEvent
        };
        
        const eventDetails = {
          ...eventDisplay,
          originalEvent: targetEvent
        };
        
        setSelectedEvent(eventDetails);
        setShowEventModal(true);
      }
    }
  }, [eventId, events]);

  // Helper function to format date range
  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate) return 'Date TBA';
    
    try {
      const start = new Date(startDate);
      const end = endDate ? new Date(endDate) : start;
      
      const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
      const startDay = start.getDate();
      const endDay = end.getDate();
      const year = start.getFullYear();
      
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
      }
    } catch {
      return 'Date TBA';
    }
  };

  // Helper function to format attendees
  const formatAttendees = (count?: number) => {
    if (!count) return 'Open to all';
    if (count >= 1000) {
      return `${Math.floor(count / 1000)}k+`;
    }
    return `${count}+`;
  };

  // Component for rendering event image with thumbnail API support
  const EventImage: React.FC<{ event: any }> = ({ event }) => {
    const eventId = event.id || event.EVENT_ID;
    const { thumbnailUrl, loading } = useEventImage(event, eventId);
    
    if (loading) {
      return (
        <div className="w-full h-72 bg-gray-200 animate-pulse flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      );
    }
    
    return (
      <img
        src={thumbnailUrl || 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=400'}
        alt={event.title || event.TITLE || 'Event'}
        className="w-full h-72 object-contain"
        onError={(e) => {
          console.error('❌ Image failed to load for event:', event.title || event.TITLE);
          e.currentTarget.src = 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=400';
        }}
      />
    );
  };

  // Handle event click - check authentication before navigating to event details
  const handleViewEventDetails = (event: any) => {
    // Check if user is logged in
    if (!user) {
      // User is not logged in, redirect to login page
      goToLogin();
      return;
    }

    // User is logged in, proceed to event details
    const eventId = event.id || event.EVENT_ID;
    const eventTitle = event.title || event.TITLE || 'Durga Puja Event';
    const eventLocation = event.location || event.LOCATION || 'Location TBA';
    
    if (eventId) {
      goToEventDetail(eventId, eventTitle, eventLocation);
    }
  };

  // Note: login success is handled inline where needed to avoid unused code

  // Transform events for display
  const pujaListings = events.map(event => {
    const eventDisplay = {
      id: event.id || event.EVENT_ID || Math.random(),
      title: event.title || event.TITLE || 'Untitled Event',
      location: event.location || event.LOCATION || event.ADDRESS || event.address || 'Location TBA',
      attendees: formatAttendees(event.expectedAttendance || event.EXPECTED_ATTENDANCE),
      date: formatDateRange(event.startDate || event.START_DATE, event.endDate || event.END_DATE),
      isFeatured: event.isFeatured || event.IS_FEATURED || false,
      originalEvent: event
    };
    
    return {
      ...eventDisplay,
      originalEvent: event
    };
  });

  // Display all events from API without any frontend filtering
  const eventsToShow = pujaListings;

  // Gallery index for modal
  const [galleryIndex, setGalleryIndex] = useState(0);
  useEffect(() => {
    if (showEventModal) {
      setGalleryIndex(0);
    }
  }, [showEventModal, selectedEvent?.id]);

  const handleExploreAll = () => {
    // Navigate to explore page
    import('../utils/navigation').then(({ goToExplore }) => goToExplore());
  };

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-b from-dpn-light-green to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-dpn-pink mx-auto mb-4" />
            <p className="font-lucy-rose text-gray-600">Loading events...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="h-8 w-8 bg-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <p className="font-lucy-rose text-red-600">Error loading events: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="search" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-4">
            Discover Durga Puja Events
          </h2>
          <p className="font-lucy-rose text-lg text-gray-600 max-w-2xl mx-auto">
            Explore upcoming Durga Puja celebrations, cultural events, and community gatherings across the network.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-6 w-6" />
            <input
              type="text"
              placeholder="Search by city, country, or puja name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-6 py-4 border border-gray-200 rounded-full bg-white focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose text-base sm:text-lg shadow-sm"
            />
          </div>
        </div>
        
        {/* Main Filter Chips */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="flex justify-center gap-4">
            {/* Location Filter - Static display showing current selection */}
            <div className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full shadow-sm">
              <MapPin className="h-5 w-5 text-dpn-light-green" />
              <span className="font-lucy-rose font-medium text-gray-800">
                {selectedCity ? selectedCity : 'Location'}
              </span>
            </div>



          </div>
        </div>

        {/* Clear Filters Section */}
        {(selectedCity || searchTerm) && (
          <div className="max-w-4xl mx-auto mb-6 text-center">
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-50 border-2 border-red-200 rounded-full hover:bg-red-100 transition-all duration-200 font-lucy-rose font-medium text-red-600 hover:text-red-700 hover:border-red-300"
            >
              <X className="h-5 w-5" />
              Clear All Filters
            </button>
          </div>
        )}

        {/* City Filters */}
        <div className="max-w-4xl mx-auto mb-8">
          <CityFilters 
            selectedCity={selectedCity} 
            onSelectCity={handleCitySelect} 
          />
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {eventsToShow.map((puja) => (
            <div
              key={puja.id}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group border border-gray-100 flex flex-col h-full"
              onClick={() => handleViewEventDetails(puja)}
            >
              {/* Event Image */}
              <div className="relative overflow-hidden rounded-t-2xl">
                <EventImage event={puja.originalEvent} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {/* Featured Badge */}
                {puja.isFeatured && (
                  <div className="absolute top-3 left-3 bg-yellow-500 text-white px-2.5 py-1 rounded-full text-xs font-medium shadow">
                    ⭐ Featured
                  </div>
                )}
                {/* Map chip if coordinates present */}
                {puja.originalEvent?.latitude && puja.originalEvent?.longitude && (
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-xs text-gray-800 flex items-center shadow">
                    <MapPin className="h-3.5 w-3.5 text-dpn-pink mr-1" /> Map
                  </div>
                )}
              </div>

              {/* Event Details */}
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-runtime text-lg font-semibold text-gray-900 mb-2 group-hover:text-dpn-pink transition-colors">
                  {puja.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {puja.originalEvent?.DESCRIPTION || puja.originalEvent?.description || 'Join us for this special Durga Puja celebration.'}
                </p>
                
                {/* Event Info */}
                <div className="grid grid-cols-3 gap-3 text-sm text-gray-600 mb-5">
                  <div className="flex items-center col-span-1">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span className="truncate" title={puja.date}>{puja.date}</span>
                  </div>
                  <div className="flex items-center col-span-2">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span className="truncate" title={puja.location}>{puja.location}</span>
                  </div>
                </div>

                {/* Card footer actions */}
                <div className="mt-auto flex items-center justify-between">
                  <button className="flex-1 bg-dpn-pink hover:bg-dpn-raspberry text-white py-2.5 px-4 rounded-lg transition-colors duration-200 text-sm font-lucy-rose font-medium">
                    View Details
                  </button>
                  {user && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        
                        console.log(puja.originalEvent);
                        // Always try to use coordinates first, fallback to location name
                        if (puja.originalEvent?.latitude && puja.originalEvent?.longitude) {
                          const lat = Number(puja.originalEvent.latitude);
                          const lng = Number(puja.originalEvent.longitude);
                          window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
                        } else {
                          // Fallback to location name search if no coordinates
                          const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(puja.location)}`;
                          window.open(url, '_blank');
                        }
                      }}
                      className="ml-2 px-3 py-2 border rounded-lg text-sm whitespace-nowrap flex items-center transition-all duration-200 bg-white border-gray-200 hover:bg-gray-50 text-gray-700"
                    >
                      <ExternalLink className="h-4 w-4 mr-1 text-gray-700" /> 
                      Map
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Explore All Events Button */}
        <div className="text-center mt-8">
          <button
            onClick={handleExploreAll}
            className="inline-flex items-center gap-2 bg-dpn-pink hover:bg-dpn-raspberry text-white px-8 py-3 rounded-lg transition-all duration-200 font-lucy-rose font-medium shadow-md"
          >
            Explore All Events
          </button>
        </div>

        {/* Advertisement Section */}
        <div className="mt-16 flex justify-center">
          <AdSense 
            adSlot="1234567893" 
            adFormat="horizontal"
            className="w-full max-w-728"
            adStyle={{ display: 'block', width: '728px', height: '90px' }}
          />
        </div>

        {/* Event Details Modal */}
        {showEventModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden max-h-[90vh] overflow-y-auto">
              {/* Modal Header - aligned with dashboard styling */}
              <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
                <div className="flex items-start space-x-3">
                  <Calendar className="h-6 w-6 text-dpn-pink mt-0.5" />
                  <div>
                    <h2 className="text-2xl font-runtime font-bold text-gray-900 mb-1 line-clamp-2">{selectedEvent.title}</h2>
                    <div className="flex flex-wrap items-center gap-2">
                      {selectedEvent.isFeatured && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Featured</span>
                      )}
                      {selectedEvent.originalEvent?.IS_VERIFIED !== undefined && (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${selectedEvent.originalEvent.IS_VERIFIED ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                          {selectedEvent.originalEvent.IS_VERIFIED ? (<><CheckCircle className="h-3.5 w-3.5 mr-1" /> Verified</>) : (<><AlertCircle className="h-3.5 w-3.5 mr-1" /> Pending</>)}
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${selectedEvent.originalEvent?.IS_ACTIVE !== false ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                        {selectedEvent.originalEvent?.IS_ACTIVE !== false ? 'Active' : (<><XCircle className="h-3.5 w-3.5 mr-1" /> Inactive</>)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500 font-lucy-rose">
                      {selectedEvent.originalEvent?.CREATED_AT && (
                        <span className="mr-3">Created: {new Date(selectedEvent.originalEvent.CREATED_AT).toLocaleString()}</span>
                      )}
                      {selectedEvent.originalEvent?.UPDATED_AT && (
                        <span>Updated: {new Date(selectedEvent.originalEvent.UPDATED_AT).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={() => setShowEventModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* Event Images Carousel */}
                {(() => {
                  const images: string[] = selectedEvent.originalEvent?.IMAGES && selectedEvent.originalEvent.IMAGES.length > 0
                    ? selectedEvent.originalEvent.IMAGES
                    : [selectedEvent.image];
                  return images && images.length > 0 ? (
                    <div className="mb-8">
                      <h3 className="font-runtime text-lg font-semibold text-black mb-4 flex items-center">
                        <Eye className="h-5 w-5 mr-2 text-dpn-pink" />
                        Event Images
                      </h3>
                      <div className="relative rounded-lg overflow-hidden">
                        <img src={images[galleryIndex]} alt={`${selectedEvent.title} - Image ${galleryIndex + 1}`} className="w-full h-64 object-cover" />
                        {images.length > 1 && (
                          <>
                            <button
                              onClick={() => setGalleryIndex((prev) => (prev - 1 + images.length) % images.length)}
                              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow"
                              aria-label="Previous image"
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setGalleryIndex((prev) => (prev + 1) % images.length)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow"
                              aria-label="Next image"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      </div>
                      {images.length > 1 && (
                        <div className="mt-3 flex gap-2 overflow-x-auto">
                          {images.map((img, idx) => (
                            <button key={idx} onClick={() => setGalleryIndex(idx)} className={`h-14 w-20 rounded overflow-hidden border ${idx === galleryIndex ? 'border-dpn-pink' : 'border-gray-200'}`} aria-label={`Show image ${idx + 1}`}>
                              <img src={img} alt={`thumb-${idx + 1}`} className="h-full w-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Description */}
                  <div>
                    <h3 className="font-runtime text-lg font-semibold text-black mb-3 flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-dpn-pink" /> About this event
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="font-lucy-rose text-gray-700 leading-relaxed text-sm">{selectedEvent.description}</p>
                    </div>
                  </div>

                  {/* Event Details */}
                  <div>
                    <h3 className="font-runtime text-lg font-semibold text-black mb-3 flex items-center">
                      <Info className="h-5 w-5 mr-2 text-dpn-pink" /> Event Details
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className="flex-1 text-sm">
                          <p className="font-lucy-rose font-medium text-gray-900">Event Date</p>
                          <p className="font-lucy-rose text-gray-600">{selectedEvent.date}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Users className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className="flex-1 text-sm">
                          <p className="font-lucy-rose font-medium text-gray-900">Expected Attendance</p>
                          <p className="font-lucy-rose text-gray-600">{selectedEvent.attendees}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div className="flex-1 text-sm">
                          <p className="font-lucy-rose font-medium text-gray-900 flex items-center justify-between">
                            <span>Location</span>
                            <button
                              onClick={async () => {
                                const text = selectedEvent.originalEvent?.ADDRESS || selectedEvent.location;
                                try {
                                  await navigator.clipboard.writeText(text || '');
                                  setCopySuccess(true);
                                  setTimeout(() => setCopySuccess(false), 2000);
                                } catch (error) {
                                  console.error('Error copying address:', error);
                                }
                              }}
                              className={`ml-2 inline-flex items-center text-xs px-2 py-1 border rounded transition-all duration-300 transform ${
                                copySuccess 
                                  ? 'bg-green-100 border-green-300 text-green-700 scale-105' 
                                  : 'bg-white border-gray-200 hover:bg-gray-100 hover:scale-105'
                              }`}
                            >
                              {copySuccess ? (
                                <>
                                  <Check className="h-3.5 w-3.5 mr-1 animate-pulse" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3.5 w-3.5 mr-1" />
                                  Copy Address
                                </>
                              )}
                            </button>
                          </p>
                          <p className="font-lucy-rose text-gray-600">{selectedEvent.location}</p>
                          {selectedEvent.originalEvent?.ADDRESS && (
                            <p className="font-lucy-rose text-xs text-gray-500 mt-1">{selectedEvent.originalEvent.ADDRESS}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Embedded Map and Action - always show with graceful fallback */}
                <div className="mt-8">
                  <h3 className="font-runtime text-lg font-semibold text-black mb-3 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-dpn-pink" /> Location Map
                  </h3>
                  
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Location header with badge */}
                    <div className="bg-gray-50 p-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex items-center">
                          <span className="font-lucy-rose font-medium">{selectedEvent.location}</span>
                        </div>
                        
                        {(!selectedEvent.originalEvent?.latitude || !selectedEvent.originalEvent?.longitude) && (
                          <span className="ml-3 text-xs bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full font-medium">
                            Approximate location
                          </span>
                        )}
                      </div>
                      
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            const lat = Number(selectedEvent.originalEvent?.latitude ?? 22.5726);
                            const lng = Number(selectedEvent.originalEvent?.longitude ?? 88.3639);
                            window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
                          }}
                          className={`text-xs flex items-center ${
                            selectedEvent.originalEvent?.latitude && selectedEvent.originalEvent?.longitude 
                              ? "text-blue-600 hover:text-blue-800" 
                              : "text-gray-400"
                          }`}
                          disabled={!selectedEvent.originalEvent?.latitude || !selectedEvent.originalEvent?.longitude}
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Google Maps
                        </button>
                        
                      </div>
                    </div>
                    
                    {/* Map container */}
                    <EventLocationViewer
                      latitude={Number(selectedEvent.originalEvent?.latitude ?? 0)}
                      longitude={Number(selectedEvent.originalEvent?.longitude ?? 0)}
                      title={selectedEvent.title}
                      address={selectedEvent.originalEvent?.ADDRESS || selectedEvent.location}
                      height="h-72"
                      showFullAddress={false}
                      className="border-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Login Popup */}
        {showLoginPopup && (
          <Login
            onClose={() => {
              setShowLoginPopup(false);
            }}
            onSwitchToSignup={() => {
              // You can implement signup logic here if needed
              setShowLoginPopup(false);
            }}
            redirectAfterLogin={undefined}
          />
        )}

        {/* Filter Modals */}





        {/* Login Prompt Modal */}
        {showLoginPrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full text-center p-8">
              <div className="flex justify-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="Durga Puja Network" 
                  className="h-24 w-24 object-contain"
                  onError={(e) => e.currentTarget.style.display = 'none'}
                />
              </div>
      
              <h2 className="font-runtime text-2xl font-bold text-black mb-2">
                Authentication Required
              </h2>
              <p className="font-lucy-rose text-gray-600 mb-6">
                Please sign in to access exclusive event details and information.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    // Redirect to login page
                    window.location.href = '/login';
                  }}
                  className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Login
                </button>
                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="w-full flex items-center justify-center space-x-2 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-lucy-rose font-medium text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SearchDiscovery;